import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import { resolveApiUrl } from '@/lib/api/resolve-url';
import type {
  User,
  UsersListParams,
  UserMetrics,
  CreateUserRequest,
  UpdateUserRequest,
  DriverAvailability,
  OperatorAvailability,
  BulkImportResult,
  Nationality
} from '../types';
import type { AssignRoleRequest, AssignRoleResponse } from '@/features/roles';

const { USERS, NATIONALITIES } = API_ENDPOINTS;

export const usersApi = {
  getAll: (params?: UsersListParams) =>
    apiClient.getPaginated<User>(USERS.LIST, params),

  getById: (id: number) => apiClient.get<User>(USERS.GET(id)),

  create: (data: CreateUserRequest) =>
    apiClient.post<{ user: User }>(USERS.CREATE, data),

  update: (id: number, data: UpdateUserRequest) =>
    apiClient.put<User>(USERS.UPDATE(id), data),

  delete: (id: number) => apiClient.delete(USERS.DELETE(id)),

  assignRole: (userId: number, data: AssignRoleRequest) =>
    apiClient.post<AssignRoleResponse>(USERS.ASSIGN_ROLE(userId), data),

  removeRole: (userId: number, data: AssignRoleRequest) =>
    apiClient.delete<AssignRoleResponse>(USERS.REMOVE_ROLE(userId), data),

  metrics: () => apiClient.get<UserMetrics>(USERS.METRICS),

  deactivate: (userId: number) => apiClient.patch(USERS.DEACTIVATE(userId)),

  unlock: (userId: number) => apiClient.patch(USERS.UNLOCK(userId)),

  activate: (userId: number) => apiClient.patch(USERS.ACTIVATE(userId)),

  bulkDelete: (
    params: { ids: number[] } | { all: true; filters: Partial<UsersListParams> }
  ) => apiClient.post(USERS.BULK_DELETE, params),

  bulkActivate: (
    params: { ids: number[] } | { all: true; filters: Partial<UsersListParams> }
  ) => apiClient.post(USERS.BULK_ACTIVATE, params),

  bulkDeactivate: (
    params: { ids: number[] } | { all: true; filters: Partial<UsersListParams> }
  ) => apiClient.post(USERS.BULK_DEACTIVATE, params),

  updateDriverAvailability: (userId: number, status: DriverAvailability) =>
    apiClient.patch(USERS.DRIVER_AVAILABILITY(userId), { status }),

  updateOperatorAvailability: (userId: number, status: OperatorAvailability) =>
    apiClient.patch(USERS.OPERATOR_AVAILABILITY(userId), { status }),

  bulkImport: (file: File, onProgress?: (n: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient
      .uploadFormData<BulkImportResult>(USERS.BULK_IMPORT, formData, onProgress)
      .then((r) => r.data);
  },

  nationalities: () => apiClient.get<Nationality[]>(NATIONALITIES.LIST),

  downloadTemplate: async () => {
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    const locale =
      typeof document !== 'undefined'
        ? (document.documentElement.lang ?? 'en')
        : 'en';
    // gitlab-advanced-sast-exclude -- resolveApiUrl() pins every request to the configured API origin and throws on anything else.
    const res = await fetch(resolveApiUrl(USERS.IMPORT_TEMPLATE), {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Accept-Language': locale
      }
    });
    if (!res.ok) throw new Error('Template download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users-import-template.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }
};
